/**
 * Pending Dealer Approval — IAdmin Tools panel.
 *
 * Lists the Caprion dealer approvals that were held back to the early hours and
 * have NOT completed yet, and lets an operator push one through immediately
 * instead of waiting for the 00:05 IST run.
 *
 * WHY THIS EXISTS
 * ---------------
 * Borrowers who reach the dealer-approval page between 22:00 and 00:05 IST have
 * their approval queued rather than sent (see
 * backend/los_app/services/dealer_approval_defer.py). Normally the scheduled run
 * clears the queue and nobody needs this screen. It matters when something goes
 * wrong: a FAILED row is a loan that is validated on our side but never approved
 * with the lender, and without a surface like this it would be invisible until a
 * customer complained.
 *
 * Only PENDING / IN_PROGRESS / FAILED rows are returned by the API — approved
 * ones are finished business and would just be noise on an actionable list.
 *
 * The approve call runs the lender request INLINE on the backend (not through
 * Celery), so the result shown here is the real outcome rather than "queued and
 * hopefully done".
 */

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface PendingApprovalRow {
    id: number;
    application_id: string;
    customer_phone: string | null;
    customer_name: string | null;
    /** 'PENDING' | 'IN_PROGRESS' | 'FAILED' — the API filters out APPROVED. */
    status: string;
    /** ISO datetime — when the scheduled run is/was due (00:05 IST). */
    due_at: string;
    attempts: number;
    last_error: string | null;
    created_at: string;
}

const formatDateTime = (value: string | null): string => {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString('en-IN');
};

const StatusPill: React.FC<{ status: string }> = ({ status }) => {
    const styles: Record<string, string> = {
        PENDING: 'bg-yellow-50 text-yellow-800 border-yellow-200',
        IN_PROGRESS: 'bg-blue-50 text-blue-800 border-blue-200',
        FAILED: 'bg-red-50 text-red-700 border-red-200',
    };
    return (
        <span
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                styles[status] || 'bg-gray-50 text-gray-700 border-gray-200'
            }`}
        >
            {status}
        </span>
    );
};

const PendingDealerApprovals: React.FC = () => {
    const [rows, setRows] = useState<PendingApprovalRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    /** id of the row currently being approved — disables just that button. */
    const [approvingId, setApprovingId] = useState<number | null>(null);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(
                `${API_BASE_URL}/api/lms/staff_ops/pending_dealer_approvals/`
            );
            setRows(res.data?.results || []);
        } catch (err: any) {
            const msg =
                err.response?.status === 403
                    ? 'You do not have permission to view this.'
                    : err.response?.data?.error || err.message || 'Failed to load';
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const approve = async (row: PendingApprovalRow) => {
        setApprovingId(row.id);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/api/lms/staff_ops/pending_dealer_approvals/${row.id}/approve/`
            );
            toast.success(
                res.data?.message || `Application ${row.application_id} approved.`
            );
        } catch (err: any) {
            // The backend answers 502 with a body when the LENDER refuses — that is
            // a real outcome, not a bug, so show its message rather than a generic
            // error. The row stays on the list so it can be retried.
            const msg =
                err.response?.data?.last_error ||
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                'Approval failed';
            toast.error(`Application ${row.application_id}: ${msg}`);
        } finally {
            setApprovingId(null);
            // Refetch either way: on success the row disappears (it is APPROVED
            // now), on failure its attempts/last_error have changed.
            fetchRows();
        }
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div>
                    <CardTitle className="text-xl">Pending Dealer Approval</CardTitle>
                    <p className="mt-1 text-sm text-gray-600">
                        Approvals held back to 00:05 IST (requested between 10:00 PM and
                        12:05 AM). Use “Approve Now” to send one to the lender
                        immediately.
                    </p>
                </div>
                <Button variant="outline" onClick={fetchRows} disabled={loading}>
                    <RefreshCw
                        className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                    />
                    Refresh
                </Button>
            </CardHeader>

            <CardContent>
                {loading && rows.length === 0 && (
                    <div className="flex items-center justify-center py-12 text-gray-500">
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Loading…
                    </div>
                )}

                {!loading && error && (
                    <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{error}</span>
                    </div>
                )}

                {!loading && !error && rows.length === 0 && (
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                        <CheckCircle2 className="h-10 w-10 text-intellend-green" />
                        <p className="font-medium text-gray-900">
                            Nothing pending
                        </p>
                        <p className="text-sm text-gray-600">
                            Every deferred dealer approval has gone through.
                        </p>
                    </div>
                )}

                {!error && rows.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                                    <th className="px-3 py-2">Application</th>
                                    <th className="px-3 py-2">Customer</th>
                                    <th className="px-3 py-2">Status</th>
                                    <th className="px-3 py-2">Scheduled for</th>
                                    <th className="px-3 py-2">Attempts</th>
                                    <th className="px-3 py-2">Last error</th>
                                    <th className="px-3 py-2">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((row) => (
                                    <tr key={row.id} className="border-b last:border-0">
                                        <td className="px-3 py-3 font-medium text-gray-900">
                                            {row.application_id}
                                        </td>
                                        <td className="px-3 py-3">
                                            <div className="text-gray-900">
                                                {row.customer_name || '-'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {row.customer_phone || '-'}
                                            </div>
                                        </td>
                                        <td className="px-3 py-3">
                                            <StatusPill status={row.status} />
                                        </td>
                                        <td className="px-3 py-3 text-gray-700">
                                            <span className="inline-flex items-center gap-1">
                                                <Clock className="h-3.5 w-3.5 text-gray-400" />
                                                {formatDateTime(row.due_at)}
                                            </span>
                                        </td>
                                        <td className="px-3 py-3 text-gray-700">
                                            {row.attempts}
                                        </td>
                                        <td className="px-3 py-3 max-w-xs text-xs text-red-600">
                                            {row.last_error || '-'}
                                        </td>
                                        <td className="px-3 py-3">
                                            <Button
                                                size="sm"
                                                onClick={() => approve(row)}
                                                disabled={approvingId !== null}
                                            >
                                                {approvingId === row.id ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        Approving…
                                                    </>
                                                ) : (
                                                    'Approve Now'
                                                )}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default PendingDealerApprovals;
